<script setup lang="ts">
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { UsersService } from '@/types/generated'
  import { ROUTES } from '@/router/routes'

  const { t } = useTranslation({
    en: {
      passwordSettings: 'Password Settings',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      save: 'Save',
      backToProfile: 'Back to Profile',
      passwordMismatch: 'Passwords do not match',
      passwordChanged: 'Password successfully changed',
      error: 'An error occurred, please try again',
      bothFieldsRequired: 'Both password fields are required'
    },
    de: {
      passwordSettings: 'Passwort-Einstellungen',
      newPassword: 'Neues Passwort',
      confirmPassword: 'Bestätigung des neuen Passworts',
      save: 'Speichern',
      backToProfile: 'Zurück zu den Grundeinstellungen',
      passwordMismatch: 'Passwortbestätigung stimmt nicht überein',
      passwordChanged: 'Passwort erfolgreich geändert',
      error: 'Ein Fehler ist aufgetreten, bitte versuchen Sie es erneut',
      bothFieldsRequired: 'Beide Passwortfelder sind erforderlich'
    }
  })

  const router = useRouter()
  const newPassword1 = ref('')
  const newPassword2 = ref('')
  const errorMessage = ref('')
  const successMessage = ref('')
  const isLoading = ref(false)

  const handleSubmit = async () => {
    errorMessage.value = ''
    successMessage.value = ''

    if (!newPassword1.value || !newPassword2.value) {
      errorMessage.value = t('bothFieldsRequired')
      return
    }

    if (newPassword1.value !== newPassword2.value) {
      errorMessage.value = t('passwordMismatch')
      return
    }

    isLoading.value = true

    try {
      await UsersService.usersChangePasswordCreate({
        requestBody: {
          new_password1: newPassword1.value,
          new_password2: newPassword2.value
        }
      })

      successMessage.value = t('passwordChanged')
      newPassword1.value = ''
      newPassword2.value = ''

      // Redirect back to profile after 2 seconds
      setTimeout(() => {
        router.push(ROUTES.APP.SETTINGS.USER.PROFILE)
      }, 2000)
    } catch (error: any) {
      errorMessage.value = error.body?.error || t('error')
    } finally {
      isLoading.value = false
    }
  }
</script>

<template>
  <SettingsPage>
    <div class="mb-3 block font-medium">{{ t('passwordSettings') }}</div>

    <form @submit.prevent="handleSubmit">
      <InputField
        v-model="newPassword1"
        type="password"
        name="new_password1"
        :label="t('newPassword')"
        autocomplete="new-password"
      />

      <InputField
        v-model="newPassword2"
        type="password"
        name="new_password2"
        :label="t('confirmPassword')"
        autocomplete="new-password"
      />

      <div
        v-if="isLoading"
        class="mt-4 flex items-center gap-3 rounded-md bg-blue-50 p-3 text-sm text-blue-700"
      >
        <div class="loading-spinner"></div>
        <span>Updating password...</span>
      </div>

      <div
        v-if="errorMessage"
        class="mt-2 rounded-md bg-red-100 p-3 text-sm text-red-700"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="successMessage"
        class="mt-2 rounded-md bg-green-100 p-3 text-sm text-green-700"
      >
        {{ successMessage }}
      </div>

      <div class="mt-6 flex items-center gap-4">
        <a
          :href="ROUTES.APP.SETTINGS.USER.PROFILE"
          class="text-sm text-gray-600 hover:text-gray-700"
          @click.prevent="router.push(ROUTES.APP.SETTINGS.USER.PROFILE)"
        >
          {{ t('backToProfile') }}
        </a>

        <button
          type="submit"
          :disabled="isLoading"
          class="rounded-md bg-purple-700 px-4 py-2 text-white hover:bg-purple-800 disabled:opacity-50"
        >
          {{ t('save') }}
        </button>
      </div>
    </form>
  </SettingsPage>
</template>

<style scoped>
  .loading-spinner {
    width: 20px;
    height: 20px;
    border: 3px solid #bfdbfe;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
